import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react'
import ReactFlow, {
  addEdge,
  Background,
  Connection,
  Controls,
  Edge,
  Node,
  useEdgesState,
  useNodesState,
  getOutgoers,
  EdgeProps,
  useReactFlow,
  Panel,
  NodeChange,
  useNodes,
} from 'reactflow'
import dagre from 'dagre'
import {
  NodeType,
  INITIAL_NODES,
  INITIAL_EDGES,
  FLOW_LAYOUT,
  EDGE_TYPES,
} from '../../../../../Utils/Constant/FlowConstants'
import CustomEdge from './components/CustomEdge'
import {DataNode, Flow, FlowStatus, LayoutedElements} from '../FlowTypes'
import EndNode, {EndNodeProps} from './components/EndNode'
import TriggerNode, {TriggerNodeProps} from './components/TriggerNode'
import SMSNode, {SMSNodeProps} from './components/SMSNode'
import TimeDelayNode, {TimeDelayNodeProps} from './components/TimeDelayNode'
import WaitForReplyNode, {WaitForReplyNodeProps} from './components/WaitForReplyNode'
import AddTagNode, {type AddTagNodeProps} from './components/AddTagNode'
import ConditionalNode, {ConditionalNodeProps} from './components/ConditionalNode'
import SidebarRight from '../SidebarRight/SidebarRight'
import SidebarLeft, {FormError, SidebarLeftHandle} from '../SidebarLeft/SidebarLeft'
import {useFlowManipulation} from './hooks/useFlowManipulation'
import {Spinner} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {setFlowDraft, setFlowName} from '../../../../../Redux/FrontEnd/FlowDraftSlice'
import {deleteNodesLogic} from './hooks/useFlowNodeDelete'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useUndoRedo} from './hooks/useUndoRedo'
import {nodes} from '@unovis/ts/components/sankey/style'
import {FlowDragDropProvider} from './DragDropContext'
import { useUpdateFlowMutation } from '../../../../../../services/cellcast.v2'

let addNodeBetweenEdgesGlobal: ReturnType<typeof useFlowManipulation>['addNodeBetweenEdges']

export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = FLOW_LAYOUT.DEFAULT_DIRECTION
): LayoutedElements => {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const maxNodeWidth = Math.max(
    ...nodes.map((node) => node.width || node.data?.width || FLOW_LAYOUT.NODE_WIDTH)
  )
  const maxNodeHeight = Math.max(
    ...nodes.map((node) => node.height || node.data?.height || FLOW_LAYOUT.NODE_HEIGHT)
  )

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 120,
    ranksep: 120,
    marginx: 100,
    marginy: 100,
  })

  nodes.forEach((node) => {
    const nodeWidth = node.width || node.data?.width || maxNodeWidth
    const nodeHeight = node.height || node.data?.height || maxNodeHeight

    dagreGraph.setNode(node.id, {
      ...node,
      width: nodeWidth,
      height: nodeHeight,
    })
  })

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  let layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)
    const nodeWidth = node.width || node.data?.width || maxNodeWidth
    const nodeHeight = node.height || node.data?.height || maxNodeHeight

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
      data: node.data,
    }
  })

  const nodeMap = Object.fromEntries(layoutedNodes.map((n) => [n.id, n]))
  const outgoingMap = {} as Record<string, Node[]>
  edges.forEach((edge) => {
    if (!outgoingMap[edge.source]) outgoingMap[edge.source] = []
    if (nodeMap[edge.target]) outgoingMap[edge.source].push(nodeMap[edge.target])
  })
  layoutedNodes.forEach((parent) => {
    const children = outgoingMap[parent.id]
    if (children && children.length > 1) {
      const parentY = parent.position.y
      const avgY = children.reduce((sum, c) => sum + c.position.y, 0) / children.length

      const newY = avgY
      children.forEach((child) => {
        child.position.y = newY
      })
    }
  })

  const lineIntersectsRect = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    rect: {x: number; y: number; width: number; height: number}
  ): boolean => {
    if (x1 >= rect.x && x1 <= rect.x + rect.width && y1 >= rect.y && y1 <= rect.y + rect.height)
      return true
    if (x2 >= rect.x && x2 <= rect.x + rect.width && y2 >= rect.y && y2 <= rect.y + rect.height)
      return true
    const intersects = (xa: number, ya: number, xb: number, yb: number): boolean => {
      const det = (x2 - x1) * (yb - ya) - (y2 - y1) * (xb - xa)
      if (det === 0) return false
      const t = ((xa - x1) * (yb - ya) - (ya - y1) * (xb - xa)) / det
      const u = ((xa - x1) * (y2 - y1) - (ya - y1) * (x2 - x1)) / det
      return t >= 0 && t <= 1 && u >= 0 && u <= 1
    }
    const sides = [
      [rect.x, rect.y, rect.x + rect.width, rect.y],
      [rect.x + rect.width, rect.y, rect.x + rect.width, rect.y + rect.height],
      [rect.x, rect.y + rect.height, rect.x + rect.width, rect.y + rect.height],
      [rect.x, rect.y, rect.x, rect.y + rect.height],
    ]
    return sides.some(([xa, ya, xb, yb]) => intersects(xa, ya, xb, yb))
  }

  const nodeMapForReroute = Object.fromEntries(layoutedNodes.map((n) => [n.id, n]))
  const reroutedEdges = edges.map((edge) => {
    const sourceNode = nodeMapForReroute[edge.source]
    const targetNode = nodeMapForReroute[edge.target]
    if (!sourceNode || !targetNode) return edge
    const x1 = sourceNode.position.x + (sourceNode.width || maxNodeWidth) / 2
    const y1 = sourceNode.position.y + (sourceNode.height || maxNodeHeight) / 2
    const x2 = targetNode.position.x + (targetNode.width || maxNodeWidth) / 2
    const y2 = targetNode.position.y + (targetNode.height || maxNodeHeight) / 2
    let bendPoints = []
    for (const node of layoutedNodes) {
      if (node.id === edge.source || node.id === edge.target) continue
      const rect = {
        x: node.position.x,
        y: node.position.y,
        width: node.width || maxNodeWidth,
        height: node.height || maxNodeHeight,
      }
      if (lineIntersectsRect(x1, y1, x2, y2, rect)) {
        bendPoints.push({
          x: x2,
          y: rect.y + rect.height + 30,
        })
      }
    }
    if (bendPoints.length > 0) {
      return {
        ...edge,
        data: {
          ...edge.data,
          bendPoints,
        },
      }
    }
    return edge
  })

  return {nodes: layoutedNodes, edges: reroutedEdges}
}

const isMeaningfulNodeChange = (changes: NodeChange[]) => {
  return changes.some(
    (change: any) =>
      change.type !== 'select' &&
      change.type !== 'position' &&
      change.type !== 'positionExtent' &&
      change.type !== 'reset' &&
      change.type !== 'dimensions' &&
      change.type !== 'zIndex' &&
      change.type !== 'remove'
  )
}

const isMeaningfulEdgeChange = (changes: any) => {
  return changes.some(
    (change: any) => change.type !== 'select' && change.type !== 'reset' && change.type !== 'remove'
  )
}

const FlowCanvas: React.FC<{payload?: Flow; isEdit: boolean}> = ({payload, isEdit}) => {
  const {t} = useI18n()
  const [showPreview, setShowPreview] = React.useState(false)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const flowName = useSelector((state: any) => state.flowDraft?.flowName)
  const [flowStatus, setFlowStatus] = React.useState(payload?.status || FlowStatus.ACTIVE)
  const [formError, setFormError] = React.useState<FormError | undefined>(undefined)
  const [replyWindowMinutes, setReplyWindowMinutes] = React.useState<number>(() => {
    const raw =
      payload?.settings && 'customerWaitingReplyWindowMinutes' in payload.settings
        ? (payload.settings as Record<string, unknown>)
            .customerWaitingReplyWindowMinutes
        : undefined
    const minutes = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(minutes) && minutes > 0 ? minutes : 60
  })

  const [selectedNodeId, setSelectedNodeId] = React.useState<string>(
    nodes.length > 0 ? nodes[0]?.id || '' : ''
  )

  const syncWaitForReplyNodeMeta = React.useCallback(
    (nodeId: string, minutes: number) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId || n.type !== NodeType.WAIT_FOR_REPLY) return n
          const data = (n.data ?? {}) as Record<string, unknown>
          const metaData = (data.metaData ?? {}) as Record<string, unknown>
          return {
            ...n,
            data: {
              ...data,
              metaData: {
                ...metaData,
                wait_for_reply: {
                  windowMinutes: minutes,
                },
              },
            },
          }
        })
      )
    },
    [setNodes]
  )

  const handleReplyWindowMinutesChange = React.useCallback(
    (minutes: number) => {
      setReplyWindowMinutes(minutes)
      if (selectedNodeId) {
        syncWaitForReplyNodeMeta(selectedNodeId, minutes)
      }
    },
    [selectedNodeId, setReplyWindowMinutes, syncWaitForReplyNodeMeta]
  )
  const {
    setNodes: setNodesProgrammatic,
    setEdges: setEdgesProgrammatic,
    setViewport,
    getNodes,
    getEdges,

  } = useReactFlow()
  const proOptions = {hideAttribution: true}
  const reactFlowInstance = useReactFlow()
  const dispatch = useDispatch()
  const draft = useSelector((state: any) => state.flowDraft?.draft)
  const [latestFlowName, setLatestFlowName] = useState(flowName)
  const sidebarLeftRef = useRef<SidebarLeftHandle>(null)
  const [updateFlow] = useUpdateFlowMutation()

  const {pushToUndoStack, undo, redo, canUndo, canRedo, isUndoRedoOperation} = useUndoRedo(
    [],
    [],
    setNodes,
    setEdges
  )

  useEffect(() => {
    if (formError?.nodes) {
      setFormError({
        ...formError,
        nodes: undefined,
      })
    }
  }, [formError?.nodes])

  useEffect(() => {
    let initialNodes =
      payload?.nodes && payload?.nodes.length > 0 ? [...payload?.nodes] : [...INITIAL_NODES]
    let initialEdges =
      payload?.edges && payload?.edges.length > 0 ? [...payload?.edges] : [...INITIAL_EDGES]

    setNodes(initialNodes)
    setEdges(initialEdges)
  }, [
    payload?.nodes,
    payload?.edges,
    isEdit,
    payload?.name,
    payload?.status,
    payload?._id,

    setNodes,
    setEdges,
  ])

  useEffect(() => {
    if (payload) {
      if (!isEdit) {
        dispatch(setFlowName(payload.name || ''))
      }
      setFlowStatus(payload.status || FlowStatus.ACTIVE)

      if (isEdit) {
        setLatestFlowName(payload.name || '')
      }

      const raw =
        payload.settings && 'customerWaitingReplyWindowMinutes' in payload.settings
          ? (payload.settings as Record<string, unknown>)
              .customerWaitingReplyWindowMinutes
          : undefined
      const minutes = typeof raw === 'number' ? raw : Number(raw)
      if (Number.isFinite(minutes) && minutes > 0) {
        setReplyWindowMinutes(minutes)
      }
    }
  }, [payload, dispatch, isEdit])

  useEffect(() => {
    if (!isEdit && draft && draft.nodes?.length > 0) {
      setNodes(draft.nodes)
      setEdges(draft.edges)
      if (!isEdit) {
        dispatch(setFlowName(draft.name))
      }
      setFlowStatus(draft.status)
      setLatestFlowName(draft.name || '')
    }
  }, [isEdit, draft])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault()
        undo(nodes, edges)
      } else if (
        (event.ctrlKey || event.metaKey) &&
        ((event.key === 'z' && event.shiftKey) || event.key === 'y' || event.key === 'Y')
      ) {
        event.preventDefault()
        redo(nodes, edges)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [undo, redo, nodes, edges])

  const draftDataRef = useRef({flowName, nodes, edges, flowStatus})
  const saveDraftTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    draftDataRef.current = {flowName, nodes, edges, flowStatus}
  }, [flowName, nodes, edges, flowStatus])

  const saveDraftDebounced = useCallback(() => {
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current)
    }

    saveDraftTimeoutRef.current = setTimeout(() => {
      if (!isEdit) {
        const {
          flowName: name,
          nodes: currentNodes,
          edges: currentEdges,
          flowStatus: status,
        } = draftDataRef.current
        dispatch(
          setFlowDraft({
            name,
            nodes: currentNodes,
            edges: currentEdges,
            status,
          })
        )
      }
    }, 800)
  }, [dispatch, isEdit])

  useEffect(() => {
    if (!isEdit) {
      saveDraftDebounced()
    }

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current)
      }
    }
  }, [flowName, nodes, edges, flowStatus, isEdit, saveDraftDebounced])

  const nodesRef = useRef(nodes)
  const edgesRef = useRef(edges)

  useEffect(() => {
    nodesRef.current = nodes
    edgesRef.current = edges
  }, [nodes, edges])

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((prevEdges) => {
        pushToUndoStack(nodesRef.current, prevEdges)
        const edgeToAdd = {...connection, type: EDGE_TYPES.SMOOTHSTEP}
        const newEdges = addEdge(edgeToAdd, prevEdges)
        const {nodes: layoutedNodes, edges: layoutedEdges} = getLayoutedElements(
          nodesRef.current,
          newEdges
        )
        setNodes(layoutedNodes)
        return layoutedEdges
      })
    },
    [pushToUndoStack]
  )

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id)
      setShowPreview(true)

      if (node.type === NodeType.WAIT_FOR_REPLY) {
        const data = (node.data ?? {}) as Record<string, unknown>
        const metaData = (data.metaData ?? {}) as Record<string, unknown>
        const waitData = metaData.wait_for_reply as {windowMinutes?: unknown} | undefined
        const nextMinutes = typeof waitData?.windowMinutes === 'number' ? waitData.windowMinutes : undefined
        if (typeof nextMinutes === 'number' && Number.isFinite(nextMinutes) && nextMinutes > 0) {
          setReplyWindowMinutes(nextMinutes)
        }
      }

      if (selectedNodeId === node.id) {
        setShowPreview(false)
        setSelectedNodeId('')
      }
    },
    [selectedNodeId]
  )

  const {addNodeBetweenEdges} = useFlowManipulation({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack,
    isUndoRedoOperation,
  })
  addNodeBetweenEdgesGlobal = addNodeBetweenEdges

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      setNodes((prevNodes) => {
        setEdges((prevEdges) => {
          pushToUndoStack(prevNodes, prevEdges)
          const {updatedNodes, updatedEdges} = deleteNodesLogic(deleted, prevNodes, prevEdges)
          setNodes(updatedNodes)
          setEdges(updatedEdges)
          setNodesProgrammatic(updatedNodes)
          setEdgesProgrammatic(updatedEdges)
          return updatedEdges
        })
        return prevNodes
      })
    },
    [pushToUndoStack]
  )

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      setEdges((prevEdges) => {
        pushToUndoStack(nodesRef.current, prevEdges)
        return prevEdges.filter((e) => !deleted.some((d) => d.id === e.id))
      })
    },
    [pushToUndoStack]
  )

  const isValidConnection = useCallback(
    (connection) => {
      const target = nodes.find((node) => node.id === connection.target)
      const hasCycle = (node: Node, visited = new Set()) => {
        if (visited.has(node.id)) return false

        visited.add(node.id)

        for (const outgoer of getOutgoers(node, nodes, edges)) {
          if (outgoer.id === connection.source) return true
          if (hasCycle(outgoer, visited)) return true
        }
        return false
      }
      if (!target) return false
      if (target.id === connection.source) return false
      return !hasCycle(target)
    },
    [nodes, edges]
  )

  const CustomEdgeWrapper = useCallback(
    (props: EdgeProps) => (
      <CustomEdge
        {...props}
        addNodeBetweenEdges={addNodeBetweenEdgesGlobal}
        isEdit={isEdit}
        payload={payload}
      />
    ),
    [isEdit, payload]
  )

  const edgeTypes = useMemo(
    () => ({
      [EDGE_TYPES.BEZIER]: CustomEdgeWrapper,
      [EDGE_TYPES.STRAIGHT]: CustomEdgeWrapper,
      [EDGE_TYPES.STEP]: CustomEdgeWrapper,
      [EDGE_TYPES.SMOOTHSTEP]: CustomEdgeWrapper,
    }),
    [CustomEdgeWrapper]
  )

  const handleBeautiful = useCallback(() => {
    pushToUndoStack(nodesRef.current, edgesRef.current)
    const {nodes: beautifiedNodes, edges: beautifiedEdges} = getLayoutedElements(
      nodesRef.current,
      edgesRef.current
    )
    setNodes(beautifiedNodes)
    setEdges(beautifiedEdges)
    window.requestAnimationFrame(() => {
      reactFlowInstance.fitView({
        padding: 0.1,
        includeHiddenNodes: true,
        minZoom: 0.7,
        maxZoom: 0.7,
      })
    })
  }, [pushToUndoStack, setNodes, setEdges, setViewport])

  const validateBeforeSave = () => {
    if (!latestFlowName.trim()) {
      setFormError({
        ...formError,
        name: 'Flow name is required',
      })
      return false
    }

    setFormError({
      ...formError,
      name: undefined,
      nodes: undefined,
    })
    return true
  }

  const updateFlowData = useCallback(
    (name: string, status: FlowStatus) => {
      if (!isEdit) {
        dispatch(setFlowName(name))
      }
      setFlowStatus(status)
      if (formError?.name && name.trim()) {
        setFormError({
          ...formError,
          name: undefined,
        })
      }
    },
    [formError]
  )

  const handleFlowNameChange = useCallback(
    (name: string) => {
      setLatestFlowName(name)
      if (!isEdit) {
        dispatch(setFlowName(name))
      }
      if (formError?.name && name.trim()) {
        setFormError({
          ...formError,
          name: undefined,
        })
      }
    },
    [dispatch, formError]
  )

  const handleAutoSaveFlow = useCallback(async () => {
    if (isEdit && payload?._id) {
      try {
        const currentNodes = getNodes()
        const currentEdges = getEdges()
        await updateFlow({
          id: payload._id,
          payload: {
            name: payload?.name,
            nodes: currentNodes as Node<DataNode<any>>[],
            edges: currentEdges,
            status: flowStatus,
          },
        }).unwrap()
      } catch (error) {
        console.error('Auto-save failed:', error)
      }
    }
  }, [
    isEdit,
    payload?._id,
    updateFlow,
    latestFlowName,
    getNodes,
    getEdges,
    flowStatus,
  ])

  const nodeTypes = useMemo(
    () => ({
      [NodeType.TRIGGER]: (props: TriggerNodeProps) => (
        <TriggerNode
          {...props}
          pushToUndoStack={pushToUndoStack}
          isUndoRedoOperation={isUndoRedoOperation}
        />
      ),
      [NodeType.SMS]: (props: SMSNodeProps) => (
        <SMSNode
          {...props}
          pushToUndoStack={pushToUndoStack}
          isUndoRedoOperation={isUndoRedoOperation}
        />
      ),
      [NodeType.DELAY]: (props: TimeDelayNodeProps) => (
        <TimeDelayNode
          {...props}
          pushToUndoStack={pushToUndoStack}
          isUndoRedoOperation={isUndoRedoOperation}
        />
      ),
      [NodeType.ADD_TAG]: (props: AddTagNodeProps) => <AddTagNode {...props} />,
      [NodeType.WAIT_FOR_REPLY]: (props: WaitForReplyNodeProps) => (
        <WaitForReplyNode {...props} />
      ),
      [NodeType.CONDITION]: (props: ConditionalNodeProps) => (
        <ConditionalNode
          {...props}
          pushToUndoStack={pushToUndoStack}
          isUndoRedoOperation={isUndoRedoOperation}
        />
      ),
      [NodeType.END]: (props: EndNodeProps) => (
        <EndNode
          {...props}
          pushToUndoStack={pushToUndoStack}
          isUndoRedoOperation={isUndoRedoOperation}
        />
      ),
    }),
    [pushToUndoStack, isUndoRedoOperation]
  )

  useEffect(() => {
    const errorHandler = (e: any) => {
      if (
        e.message &&
        (e.message.includes('ResizeObserver loop completed with undelivered notifications') ||
          e.message.includes('ResizeObserver loop limit exceeded') ||
          e.message.includes('ResizeObserver loop'))
      ) {
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay')
        if (resizeObserverErr) {
          resizeObserverErr.style.display = 'none'
        }

        e.preventDefault()
        e.stopImmediatePropagation()
        return false
      }
    }

    const originalError = console.error
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' &&
        (args[0].includes('ResizeObserver loop') || args[0].includes('ResizeObserver limit'))
      ) {
        return
      }
      originalError.apply(console, args)
    }

    window.addEventListener('error', errorHandler)

    return () => {
      window.removeEventListener('error', errorHandler)
      console.error = originalError
    }
  }, [])

  const onNodesChangePatched = useCallback(
    (changes) => {
      if (isMeaningfulNodeChange(changes)) {
        pushToUndoStack(nodesRef.current, edgesRef.current)
      }
      onNodesChange(changes)
    },
    [pushToUndoStack, onNodesChange]
  )

  const onEdgesChangePatched = useCallback(
    (changes) => {
      if (isMeaningfulEdgeChange(changes)) {
        pushToUndoStack(nodesRef.current, edgesRef.current)
      }
      onEdgesChange(changes)
    },
    [pushToUndoStack, onEdgesChange]
  )

  useEffect(() => {
    if (showPreview && selectedNodeId) {
      const found = nodes.find((n) => n.id === selectedNodeId)
      if (!found) {
        setShowPreview(false)
        setSelectedNodeId('')
      }
    }
  }, [showPreview, selectedNodeId, nodes])

  return (
    <FlowDragDropProvider addNodeBetweenEdges={addNodeBetweenEdgesGlobal}>
      <div className='row' style={{position: 'relative'}}>
        <div className='col-12 col-lg-3 border-end p-2 p-lg-4'>
          <SidebarLeft
            ref={sidebarLeftRef}
            payload={payload}
            isEdit={isEdit}
            onFlowDataChange={updateFlowData}
            formError={formError}
            onFlowNameChange={handleFlowNameChange}
            replyWindowMinutes={replyWindowMinutes}
          />
        </div>

        <div className={`col-12 ${showPreview ? 'col-lg-6' : 'col-lg-9'} p-0`}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChangePatched}
            onEdgesChange={onEdgesChangePatched}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            edgeTypes={edgeTypes}
            defaultViewport={{x: 375, y: 50, zoom: 0.7}}
            nodesDraggable={false}
            isValidConnection={isValidConnection}
            style={{minHeight: '600px'}}
            proOptions={proOptions}
          >
            <Controls />
            <Background />
            <Panel position='bottom-right' className='d-flex justify-content-end gap-2 p-2 p-lg-3'>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id='tooltip-undo'>{t('FLOW.UNDO')} (Ctrl+Z)</Tooltip>}
              >
                <button
                  className='btn btn-outline btn-outline-primary px-2 px-lg-4 py-2 py-lg-3 d-flex align-items-center gap-2'
                  onClick={() => undo(nodes, edges)}
                  disabled={!canUndo || sidebarLeftRef.current?.isLoading}
                  title={canUndo ? `${t('FLOW.UNDO')} (Ctrl+Z)` : t('FLOW.UNDO_DISABLED')}
                >
                  <i className='fas fa-undo'></i>
                  <span className='d-none d-sm-inline'>{t('FLOW.UNDO')}</span>
                </button>
              </OverlayTrigger>

              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id='tooltip-redo'>{t('FLOW.REDO')}(Ctrl+Y)</Tooltip>}
              >
                <button
                  className='btn btn-outline btn-outline-primary px-2 px-lg-4 py-2 py-lg-3 d-flex align-items-center gap-2'
                  onClick={() => redo(nodes, edges)}
                  disabled={!canRedo || sidebarLeftRef.current?.isLoading}
                  title={canRedo ? `${t('FLOW.REDO')} (Ctrl+Y)` : t('FLOW.REDO_DISABLED')}
                >
                  <i className='fas fa-redo'></i>
                  <span className='d-none d-sm-inline'>{t('FLOW.REDO')}</span>
                </button>
              </OverlayTrigger>

              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='tooltip-beautify'>{t('FLOW.INSTANCE.BEAUTIFY_DESC')}</Tooltip>
                }
              >
                <button
                  className='btn btn-outline btn-outline-primary px-2 px-lg-4 py-2 py-lg-3 d-flex align-items-center gap-2'
                  onClick={handleBeautiful}
                  disabled={sidebarLeftRef.current?.isLoading}
                >
                  <i className='fas fa-magic'></i>
                  <span className='d-none d-sm-inline'>{t('FLOW.INSTANCE.BEAUTIFY')}</span>
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id='tooltip-save'>{t('FLOW.INSTANCE.SAVE_DESC')}</Tooltip>}
              >
                <button
                  className='btn btn-primary px-2 px-lg-4 py-2 py-lg-3 d-flex align-items-center gap-2'
                  onClick={() => {
                    const isValid = validateBeforeSave()
                    if (isValid) {
                      sidebarLeftRef.current?.submitForm()
                    }
                  }}
                  disabled={sidebarLeftRef.current?.isLoading}
                >
                  <i className='fas fa-save'></i>
                  <span className='d-none d-sm-inline'>{isEdit ? 'Update' : 'Save'}</span>
                  {sidebarLeftRef.current?.isLoading && <Spinner size='sm' className='ms-2' />}
                </button>
              </OverlayTrigger>
            </Panel>
          </ReactFlow>
        </div>

        {showPreview && (
          <div className='col-12 col-lg-3 border-start p-2 p-lg-4'>
            <SidebarRight
              id={selectedNodeId}
              onClose={() => {
                setShowPreview(false)
                setSelectedNodeId('')
              }}
              onAutoSaveFlow={handleAutoSaveFlow}
              replyWindowMinutes={replyWindowMinutes}
              onReplyWindowMinutesChange={handleReplyWindowMinutesChange}
            />
          </div>
        )}
      </div>
    </FlowDragDropProvider>
  )
}

export default FlowCanvas
